import React from 'react';
import {
  FiUploadCloud,
  FiRefreshCw,
  FiSliders,
  FiExternalLink,
} from 'react-icons/fi';

import { type ColumnDef } from '@/components/organism/Table';
import {
  Cell,
  PillCell,
  RowActionsMenu,
  SMART_COOLER_DEVICE_STATUS_PILL_MAP,
  SMART_COOLER_SYSTEM_STATE_PILL_MAP,
} from '@/components/atoms';

import {
  SMART_COOLER_PROPS,
  SMART_COOLER_STATUS_VALUES,
} from '@/constant/smartCooler/smartCooler.constant';
import { SMART_COOLER_TEST_IDS as TID } from '@/constant/testIds/smartCooler.testIds';
import { getDateTime, getDateTimeAgo } from '@/utils/dateTime';
import { tid } from '@/utils/testId';
import { ISmartCooler } from './smartCooler.interface';

// ─── Column definitions ───────────────────────────────────────────────────────
export function buildColumns(
  isCopy: string,
  onCopyClick: (key: string) => void,
  onUploadLogs: (item: ISmartCooler) => void,
  onViewDetails?: (item: ISmartCooler) => void,
): ColumnDef<ISmartCooler>[] {
  const makeCopy = (copyKey: string) => ({ copyKey, isCopy, onCopyClick });

  return [
    {
      accessor: 'coolerId',
      header: SMART_COOLER_PROPS.LABELS.COOLER_ID,
      sorting: false,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_COOLER_ID(`${idx}`))}
          value={item.coolerId}
          tooltip
          variant="mono"
          size="text-[11px]"
          copy={makeCopy(`cooler-${item.coolerId}`)}
          className="w-[140px]"
        />
      ),
    },
    {
      accessor: 'nativeId',
      header: SMART_COOLER_PROPS.LABELS.NATIVE_ID,
      sorting: false,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_NATIVE_ID(`${idx}`))}
          value={item.nativeId}
          variant="primary"
          copy={makeCopy(`native-${item.coolerId}`)}
          className="w-[120px] font-semibold"
        />
      ),
    },
    {
      accessor: 'deviceId',
      header: SMART_COOLER_PROPS.LABELS.DEVICE_ID,
      sorting: false,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_DEVICE_ID(`${idx}`))}
          value={item.deviceId}
          variant="primary"
          copy={makeCopy(`dev-${item.coolerId}`)}
          className="w-[140px]"
        />
      ),
    },
    {
      accessor: 'operatorId',
      header: SMART_COOLER_PROPS.LABELS.OPERATOR_INFO,
      sorting: false,
      render: (item, idx) => (
        <div
          className="flex flex-col gap-0.5"
          data-testid={tid(TID.COL_OPERATOR_INFO(`${idx}`))}
        >
          <Cell
            value={item.operatorId}
            variant="primary"
            tooltip
            copy={makeCopy(`op-${item.coolerId}`)}
            className="w-[160px]"
          />
          <Cell
            value={item.partnerUid}
            variant="muted"
            size="text-[11px]"
            className="w-[140px]"
          />
        </div>
      ),
    },
    {
      accessor: 'marketId',
      header: SMART_COOLER_PROPS.LABELS.MARKET_ID,
      sorting: false,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_MARKET_ID(`${idx}`))}
          value={item.marketId}
          variant="primary"
          copy={makeCopy(`mkt-${item.coolerId}`)}
          className="w-[120px]"
        />
      ),
    },
    {
      accessor: 'deviceType',
      header: SMART_COOLER_PROPS.LABELS.DEVICE_TYPE,
      sorting: false,
      render: (item, idx) =>
        item.deviceType ? (
          <PillCell
            testId={tid(TID.COL_DEVICE_TYPE(`${idx}`))}
            value={item.deviceType}
            config={{
              bg: 'bg-blue-50 dark:bg-blue-900/20',
              text: 'text-blue-700 dark:text-blue-300',
              border: 'border-blue-200 dark:border-blue-700',
            }}
            shape="badge"
            size="text-[11px]"
            className="font-semibold border uppercase"
          />
        ) : (
          <Cell value={undefined} variant="muted" />
        ),
    },
    {
      accessor: 'status',
      header: SMART_COOLER_PROPS.LABELS.STATUS,
      sorting: false,
      render: (item, idx) => (
        <PillCell
          testId={tid(TID.COL_STATUS(`${idx}`))}
          value={item.status}
          map={SMART_COOLER_DEVICE_STATUS_PILL_MAP}
          dot
          shape="pill"
          size="text-[11px]"
          className="font-semibold border"
        />
      ),
    },
    {
      accessor: 'systemState',
      header: SMART_COOLER_PROPS.LABELS.SYSTEM_STATE,
      sorting: false,
      render: (item, idx) => (
        <PillCell
          testId={tid(TID.COL_SYSTEM_STATE(`${idx}`))}
          value={item.systemState}
          map={SMART_COOLER_SYSTEM_STATE_PILL_MAP}
          dot
          shape="badge"
          size="text-[11px]"
          className="font-semibold border"
        />
      ),
    },
    {
      accessor: 'updatedOn',
      header: SMART_COOLER_PROPS.LABELS.UPDATED_ON,
      sorting: true,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_UPDATED_ON(`${idx}`))}
          value={item.updatedOn}
          variant="muted"
          size="text-[11px]"
          tooltip={getDateTime(item.updatedOn)}
          tooltipPlacement="top"
        >
          {item.updatedOn ? getDateTimeAgo(item.updatedOn) : '—'}
        </Cell>
      ),
    },
    {
      accessor: 'createdOn',
      header: SMART_COOLER_PROPS.LABELS.CREATED_ON,
      sorting: true,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_CREATED_ON(`${idx}`))}
          value={item.createdOn}
          format="datetime"
          variant="muted"
          size="text-[11px]"
        />
      ),
    },
    {
      accessor: 'action',
      header: SMART_COOLER_PROPS.LABELS.ACTIONS,
      sorting: false,
      render: (item, idx) => {
        const canUpload =
          item.status !== SMART_COOLER_STATUS_VALUES.DISCONNECTED;
        return (
          <RowActionsMenu
            testId={`smart-cooler-col-actions-${idx}`}
            menuTitle="Device Actions"
            actions={[
              {
                label: SMART_COOLER_PROPS.LABELS.VIEW_DETAILS,
                icon: <FiExternalLink className="w-4 h-4" />,
                onClick: () => onViewDetails?.(item),
              },
              {
                label: SMART_COOLER_PROPS.LABELS.UPLOAD_LOGS,
                icon: <FiUploadCloud className="w-4 h-4" />,
                disabled: !canUpload,
                tooltip: canUpload
                  ? SMART_COOLER_PROPS.TOOLTIPS.UPLOAD_LOGS
                  : SMART_COOLER_PROPS.TOOLTIPS.UPLOAD_UNAVAILABLE,
                onClick: () => onUploadLogs(item),
              },
              {
                label: SMART_COOLER_PROPS.LABELS.RESTART,
                icon: <FiRefreshCw className="w-4 h-4" />,
                disabled: true,
                divider: true,
                sectionLabel: 'Coming Soon',
                tooltip: SMART_COOLER_PROPS.TOOLTIPS.COMING_SOON,
              },
              {
                label: SMART_COOLER_PROPS.LABELS.OTHERS,
                icon: <FiSliders className="w-4 h-4" />,
                disabled: true,
                tooltip: SMART_COOLER_PROPS.TOOLTIPS.COMING_SOON,
              },
            ]}
          />
        );
      },
    },
  ];
}
